import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SEZIONI, sezioniDelRuolo } from "@/lib/accessi";
import { messaggioDi } from "@/lib/messaggio-errore";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronRight, UserPlus } from "lucide-react";
import StatusPill from "@/components/common/StatusPill";

const ROLES = [
  { code: "titolare", label: "Titolare" },
  { code: "amministrazione", label: "Amministrazione" },
  { code: "venditore", label: "Venditore" },
  { code: "montatore", label: "Montatore" },
];

const RUOLO_STATO = {
  titolare: "info",
  amministrazione: "neutro",
  venditore: "ok",
  montatore: "attenzione",
};

const nomeDi = (u) => (u.nome ? `${u.nome} ${u.cognome || ""}`.trim() : u.email);

/** «Tutte e due le sedi» · «Vasto Marina» — cosa vede, in parole. */
function sediInParole(u, sedi) {
  if (u.ruolo === "titolare" || !u.sedi_viste) return sedi.length === 2 ? "Tutte e due le sedi" : "Tutte le sedi";
  return sedi.filter((s) => u.sedi_viste.includes(s.id)).map((s) => s.nome).join(", ") || "—";
}

/** «Tutto il suo ruolo» · «Senza Messaggi, Calendario». */
function sezioniInParole(u) {
  const spente = u.ruolo === "titolare" ? [] : (u.sezioni_spente || []).filter((s) => sezioniDelRuolo(u.ruolo).includes(s));
  if (!spente.length) return "Tutto il suo ruolo";
  return `Senza ${SEZIONI.filter((s) => spente.includes(s.id)).map((s) => s.label).join(", ")}`;
}

/**
 * Collaboratori e permessi (0042). Il titolare aggiunge una persona con l'invito,
 * le toglie e le ridà l'accesso, sceglie ruolo, sedi e sezioni. Le spunte non
 * sono una comodità del menu: una sezione spenta qui è chiusa nel database.
 */
export default function UsersTab() {
  const { profile } = useAuth();
  const isTitolare = profile?.ruolo === "titolare";
  const [users, setUsers] = useState(null);
  const [sedi, setSedi] = useState([]);
  const [errore, setErrore] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aperta, setAperta] = useState(null);
  const [form, setForm] = useState({
    email: "",
    nome: "",
    cognome: "",
    ruolo: "venditore",
    sede_id: "",
  });

  const load = () =>
    Promise.all([api.get("/utenti"), api.get("/sedi")])
      .then(([u, s]) => {
        setUsers(u.data || []);
        setSedi(s.data || []);
      })
      .catch((e) => setErrore(messaggioDi(e)));

  useEffect(() => {
    if (isTitolare) load();
  }, [isTitolare]);

  if (!isTitolare) {
    return (
      <Card className="p-6 bg-muted">
        <p className="text-sm text-muted-foreground">
          Collaboratori e permessi sono riservati al ruolo <strong>titolare</strong>.
        </p>
      </Card>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/utenti/invita", form);
      toast.success(
        r?.data?.rimandato
          ? "Aveva già un invito in attesa: gliel'ho rimandato. Ruolo e sedi restano quelli del suo nome nell'elenco."
          : "Invito inviato via email",
      );
      setOpen(false);
      setForm({ email: "", nome: "", cognome: "", ruolo: "venditore", sede_id: "" });
      load();
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 429
          ? "Supabase limita a 2 email/ora sugli inviti in progetti nuovi. Alza il limite dalla dashboard e riprova."
          : messaggioDi(e);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const responsabileDi = (u) => sedi.filter((s) => s.responsabile_id === u.id);

  return (
    <section className="space-y-5" data-testid="settings-users">
      <div className="flex flex-wrap items-start justify-between gap-group">
        <div className="min-w-0 max-w-2xl">
          <h2 className="text-lg font-semibold text-foreground">Collaboratori e permessi</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Chi entra nel gestionale, di quali sedi vede i clienti e quali sezioni usa. Una sezione
            spenta qui è chiusa davvero: quei dati non gli arrivano, nemmeno cercandoli.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-invite-user">
              <UserPlus className="h-4 w-4 mr-2" />
              Invita una persona
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="invite-user-modal" className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Invita un nuovo collaboratore</DialogTitle>
              <DialogDescription>
                Riceverà un'email con il link per impostare la password. Il ruolo decide cosa vede;
                sedi e sezioni le stringi dopo, dal suo nome nell'elenco.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="in-nome">Nome</Label>
                  <Input
                    id="in-nome"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    data-testid="input-user-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="in-cog">Cognome</Label>
                  <Input
                    id="in-cog"
                    value={form.cognome}
                    onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                    data-testid="input-user-cognome"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="in-email">Email</Label>
                <Input
                  id="in-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="input-user-email"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Ruolo</Label>
                  <Select value={form.ruolo} onValueChange={(v) => setForm({ ...form, ruolo: v })}>
                    <SelectTrigger data-testid="select-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.code} value={r.code} data-testid={`role-${r.code}`}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sede</Label>
                  <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                    <SelectTrigger data-testid="select-user-sede">
                      <SelectValue placeholder="Seleziona sede" />
                    </SelectTrigger>
                    <SelectContent>
                      {sedi.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`sede-${s.codice}`}>
                          {s.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="btn-invite-cancel">
                Annulla
              </Button>
              <Button onClick={submit} disabled={busy || !form.email || !form.ruolo || !form.sede_id} data-testid="submit-invite-user">
                {busy ? "Invio…" : "Invia invito"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {errore ? (
        <p className="text-sm text-danger-foreground">{errore}</p>
      ) : users === null ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : (
        <Card className="overflow-hidden" data-testid="users-table">
          {users.length === 0 ? (
            <div className="px-card py-card text-sm text-muted-foreground">
              Nessun collaboratore ancora: invita la prima persona dal bottone qui sopra.
            </div>
          ) : (
            <ul className="divide-y">
              {users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => setAperta(u)}
                    className="flex w-full min-h-11 items-center gap-group px-card py-group text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    data-testid={`users-row-${u.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-inside">
                        <span className="truncate font-medium text-foreground">{nomeDi(u)}</span>
                        <StatusPill stato={RUOLO_STATO[u.ruolo] || "neutro"} dot={false}>
                          {ROLES.find((r) => r.code === u.ruolo)?.label || u.ruolo}
                        </StatusPill>
                        {u.attivo ? null : (
                          <StatusPill stato="neutro" dot={false}>
                            Accesso tolto
                          </StatusPill>
                        )}
                        {u.attivo && u.invito_in_attesa ? (
                          <StatusPill stato="attenzione" dot={false} data-testid={`users-invito-${u.id}`}>
                            Invito in attesa
                          </StatusPill>
                        ) : null}
                      </div>
                      <div className="mt-micro truncate text-xs text-muted-foreground">{u.email}</div>
                      <div className="mt-micro text-xs text-muted-foreground">
                        {sediInParole(u, sedi)} · {sezioniInParole(u)}
                        {responsabileDi(u).length
                          ? ` · Responsabile di ${responsabileDi(u).map((s) => s.nome).join(" e ")}`
                          : ""}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Sheet open={Boolean(aperta)} onOpenChange={(v) => (v ? null : setAperta(null))}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="permessi-pannello">
          {aperta ? (
            <Permessi
              key={aperta.id}
              persona={aperta}
              sedi={sedi}
              sonoIo={aperta.id === profile?.id}
              onSalvato={() => {
                setAperta(null);
                load();
              }}
              onAnnulla={() => setAperta(null)}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </section>
  );
}

/** Chi non ha ancora scelto la password: il link dell'invito vale 24 ore, qui se ne manda uno nuovo. */
function RimandaInvito({ persona }) {
  const [invio, setInvio] = useState(false);
  const rimanda = async () => {
    setInvio(true);
    try {
      const { email, nome, cognome, ruolo, sede_id } = persona;
      await api.post("/utenti/invita", { email, nome, cognome, ruolo, sede_id });
      toast.success(`Invito rimandato a ${email}. Il link nuovo vale 24 ore, quello di prima non vale più.`);
    } catch (e) {
      toast.error(
        e?.response?.status === 429
          ? "Troppi inviti in poco tempo: riprova fra un'ora."
          : messaggioDi(e),
      );
    } finally {
      setInvio(false);
    }
  };
  return (
    <div className="space-y-group rounded-md bg-muted px-group py-group" data-testid="permessi-invito">
      <p className="text-sm text-foreground">
        Non ha ancora scelto la password. Se il link dell'email è scaduto (vale 24 ore) o non lo trova, mandagliene uno nuovo.
      </p>
      <Button variant="outline" className="min-h-11 sm:min-h-9" onClick={rimanda} disabled={invio} data-testid="permessi-rimanda-invito">
        {invio ? "Invio…" : "Rimanda l'invito"}
      </Button>
    </div>
  );
}

/** Il pannello di una persona: si cambia tutto, si salva una volta. */
function Permessi({ persona, sedi, sonoIo, onSalvato, onAnnulla }) {
  const [ruolo, setRuolo] = useState(persona.ruolo);
  const [attivo, setAttivo] = useState(persona.attivo);
  const [spente, setSpente] = useState(persona.sezioni_spente || []);
  const [viste, setViste] = useState(persona.sedi_viste || sedi.map((s) => s.id));
  const [responsabile, setResponsabile] = useState(
    sedi.filter((s) => s.responsabile_id === persona.id).map((s) => s.id),
  );
  const [salvo, setSalvo] = useState(false);

  const titolare = ruolo === "titolare";
  const delRuolo = sezioniDelRuolo(ruolo);
  const puoEssereResponsabile = ruolo === "titolare" || ruolo === "venditore";
  // Chi resta responsabile di una sede deve vederla, con Clienti e Calendario: il database
  // altrimenti gli toglie la responsabilità (0042). Qui quelle spunte restano ferme.
  const responsabileAttivo = puoEssereResponsabile && attivo && responsabile.length > 0;
  const BLOCCATE_DAL_RESPONSABILE = ["clienti", "calendario"];

  // Il ruolo preimposta le spunte: cambiarlo riparte da tutto ciò che il ruolo dà.
  const cambiaRuolo = (r) => {
    setRuolo(r);
    setSpente([]);
  };
  const tocca = (lista, id, on) => (on ? [...new Set([...lista, id])] : lista.filter((x) => x !== id));

  const salva = async () => {
    setSalvo(true);
    try {
      // «Tutte» vuol dire ogni sede che esiste oggi spuntata; si mandano solo sedi vere.
      const vere = viste.filter((id) => sedi.some((s) => s.id === id));
      const tutte = sedi.every((s) => vere.includes(s.id));
      await api.patch(`/utenti/${persona.id}`, {
        ruolo,
        attivo,
        sezioni_spente: titolare ? [] : spente.filter((s) => delRuolo.includes(s)),
        sedi_viste: titolare || tutte ? null : vere,
      });
      // Chi non entra più, o non vende, non riceve i clienti nuovi della sede: Cal li
      // manderebbe a un account spento (cal-webhook ripiega sul titolare solo se è vuoto).
      const resp = puoEssereResponsabile && attivo ? responsabile : [];
      for (const s of sedi) {
        const era = s.responsabile_id === persona.id;
        const sara = resp.includes(s.id);
        if (era !== sara) await api.put(`/sedi/${s.id}/responsabile`, { responsabile_id: sara ? persona.id : null });
      }
      toast.success(`Permessi di ${nomeDi(persona)} salvati. Sui dati valgono subito; il suo menu cambia quando ricarica la pagina.`);
      onSalvato();
    } catch (e) {
      toast.error(messaggioDi(e));
    } finally {
      setSalvo(false);
    }
  };

  return (
    <>
    <div className="flex-1 space-y-card overflow-y-auto p-card">
      <SheetHeader className="pr-section text-left">
        <SheetTitle>{nomeDi(persona)}</SheetTitle>
        <SheetDescription>{persona.email}</SheetDescription>
      </SheetHeader>

      {persona.attivo && persona.invito_in_attesa ? <RimandaInvito persona={persona} /> : null}

      <div className="space-y-inside">
        <Label htmlFor="permessi-ruolo">Ruolo</Label>
        <Select value={ruolo} onValueChange={cambiaRuolo}>
          <SelectTrigger id="permessi-ruolo" data-testid="permessi-ruolo">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r.code} value={r.code}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Il ruolo decide il massimo che vede. Per dargli di più, si cambia il ruolo.
        </p>
      </div>

      <div className="flex min-h-11 items-start justify-between gap-group">
        <div>
          <Label htmlFor="permessi-attivo" className="text-sm text-foreground">
            Può entrare
          </Label>
          <p className="mt-micro text-xs text-muted-foreground">
            {attivo
              ? "Se lo spegni non vede più niente; i suoi clienti e il suo lavoro restano dove sono."
              : "Accesso tolto. Lo riaccendi quando vuoi: ritrova tutto com'era."}
          </p>
        </div>
        <Switch id="permessi-attivo" checked={attivo} onCheckedChange={setAttivo} data-testid="permessi-attivo" />
      </div>

      {titolare ? (
        <p className="rounded-md bg-muted px-group py-group text-sm text-muted-foreground" data-testid="permessi-titolare">
          Il titolare vede tutto, in tutte e due le sedi: sezioni e sedi non si stringono.
          {sonoIo ? " Il tuo accesso da titolare lo puoi togliere solo se c'è un altro titolare." : ""}
        </p>
      ) : (
        <>
          <fieldset className="space-y-inside">
            <legend className="text-sm font-medium text-foreground">Sedi che vede</legend>
            {sedi.map((s) => (
              <div key={s.id} className="flex min-h-11 items-center gap-inside">
                <Checkbox
                  id={`permessi-sede-${s.codice}`}
                  checked={viste.includes(s.id)}
                  disabled={(viste.length === 1 && viste.includes(s.id)) || (responsabileAttivo && responsabile.includes(s.id))}
                  onCheckedChange={(v) => setViste((xs) => tocca(xs, s.id, v === true))}
                  data-testid={`permessi-sede-${s.codice}`}
                />
                <Label htmlFor={`permessi-sede-${s.codice}`} className="cursor-pointer text-sm text-foreground">
                  {s.nome}
                </Label>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              Almeno una. Per chiudere tutto si spegne «Può entrare». La sede di cui è responsabile resta
              spuntata: prima si toglie la responsabilità.
            </p>
          </fieldset>

          <fieldset className="space-y-inside">
            <legend className="text-sm font-medium text-foreground">Sezioni</legend>
            <div className="grid gap-x-group sm:grid-cols-2">
              {SEZIONI.map((s) => {
                const nelRuolo = delRuolo.includes(s.id);
                return (
                  <div key={s.id} className="flex min-h-11 items-center gap-inside">
                    <Checkbox
                      id={`permessi-sezione-${s.id}`}
                      checked={nelRuolo && !spente.includes(s.id)}
                      disabled={!nelRuolo || (responsabileAttivo && BLOCCATE_DAL_RESPONSABILE.includes(s.id))}
                      onCheckedChange={(v) => setSpente((xs) => tocca(xs, s.id, v !== true))}
                      data-testid={`permessi-sezione-${s.id}`}
                    />
                    <Label
                      htmlFor={`permessi-sezione-${s.id}`}
                      className={nelRuolo ? "cursor-pointer text-sm text-foreground" : "text-sm text-muted-foreground"}
                    >
                      {s.label}
                      {nelRuolo ? null : <span className="sr-only"> (non nel ruolo)</span>}
                    </Label>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Le voci in grigio non sono nel suo ruolo. Una sezione spenta sparisce dal menu e i suoi dati
              non gli arrivano. Al responsabile di una sede Clienti e Calendario restano accese.
            </p>
          </fieldset>
        </>
      )}

      {puoEssereResponsabile ? (
        <fieldset className="space-y-inside">
          <legend className="text-sm font-medium text-foreground">Responsabile di sede</legend>
          {sedi.map((s) => {
            const altro = s.responsabile_id && s.responsabile_id !== persona.id;
            return (
              <div key={s.id} className="flex min-h-11 items-center gap-inside">
                <Checkbox
                  id={`permessi-resp-${s.codice}`}
                  checked={responsabile.includes(s.id)}
                  onCheckedChange={(v) => {
                    setResponsabile((xs) => tocca(xs, s.id, v === true));
                    // Il responsabile vede la sua sede: senza, i clienti gli arriverebbero invisibili.
                    if (v === true) setViste((xs) => tocca(xs, s.id, true));
                  }}
                  data-testid={`permessi-resp-${s.codice}`}
                />
                <Label htmlFor={`permessi-resp-${s.codice}`} className="cursor-pointer text-sm text-foreground">
                  {s.nome}
                  {altro && responsabile.includes(s.id) ? (
                    <span className="block text-xs text-muted-foreground">prende il posto del responsabile di oggi</span>
                  ) : null}
                </Label>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">
            Riceve i clienti nuovi che prenotano su quella sede e ne vede tutti i clienti, anche quelli
            seguiti da altri.
          </p>
        </fieldset>
      ) : null}

    </div>
      {/* Il pannello è lungo (a 390 px più di due schermate): le spunte scorrono, salva e annulla restano fermi. */}
      <div className="flex flex-wrap justify-end gap-inside border-t px-card py-group">
        <Button variant="outline" onClick={onAnnulla} disabled={salvo}>
          Annulla
        </Button>
        <Button onClick={salva} disabled={salvo} data-testid="permessi-salva">
          {salvo ? "Salvo…" : "Salva i permessi"}
        </Button>
      </div>
    </>
  );
}
